import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Checkbox, Snackbar, Divider } from "@material-ui/core";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { Alert } from "@material-ui/lab";

import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import AutoForm from "../form/functional/AutoForm";
import { cloneDeep } from "lodash";

import "../grid/layout/DataGridMomSysOrderPurchase.css";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

function MomSysOrderPurchaseDetailPstMaterialRegisterDialog(props) {
    const {
        id,
        schema, // MomSysUglOrderPurchaseDetail schema

        moveable,
        resizable,
        fullWidth,
        maxWidth,

        onClose,
        onInitialized,
        onSuccess,

        onDialogUpdateDetail,
        orderPurchaseMstSelect,

        customizeErrorMsgCallback,

        ...others
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();

    const title = t("dialog.title.orderPurchaseDetail.pstMaterialRegister");

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const [modifiedSchema, setModifiedSchema] = useState();

    const [dialogInstance, setDialogInstance] = useState();
    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [formData, setFormData] = useState({});
    const [keepOpen, setKeepOpen] = useState(false);

    const [orderPurchaseDetailTargetGrid, setOrderPurchaseDetailTargetGrid] = useState();
    const [orderPurchaseDetailTargetSelect, setOrderPurchaseDetailTargetSelect] = useState();
    const [orderPurchaseDetailTargetApi, setOrderPurchaseDetailTargetApi] = useState();

    if (schema && !schema.columnMap) {
        let columnMap = {};

        for (const column of schema.columns) {
            columnMap[column.name] = column;
        }

        schema.columnMap = columnMap;
    }

    useEffect(() => {
        let mounted = true;

        (async () => {
            if (orderPurchaseDetailTargetGrid && mounted) {
                orderPurchaseDetailTargetGrid.selectionChanged.addHandler((grid, event) => {
                    setOrderPurchaseDetailTargetSelect(grid.selectedItems[0]);
                });

                orderPurchaseDetailTargetGrid.itemsSourceChanged.addHandler((grid, event) => {
                    setOrderPurchaseDetailTargetSelect(grid.selectedItems[0]);
                });
            }
        })();

        return () => {
            mounted = false;
        };
    }, [orderPurchaseDetailTargetGrid]);

    useEffect(() => {
        if (schema && orderPurchaseDetailTargetSelect) {
            setFormData((formData) => {
                let newFormData = {
                    ...formData,

                    orderPurchaseId: orderPurchaseMstSelect.orderPurchaseId,
                    orderBuyerId: orderPurchaseDetailTargetSelect.orderBuyerId,
                    orderBuyerDetailId: orderPurchaseDetailTargetSelect.orderBuyerDetailId,
                    materialCode: orderPurchaseDetailTargetSelect.componentCode, // Note : componentCode
                    materialCodeVer: orderPurchaseDetailTargetSelect.componentCodeVer, // Note : componentCodeVer
                    qty: orderPurchaseDetailTargetSelect.qtyOrderPurchase,
                    qtyInput: 0,

                    componentCode: orderPurchaseDetailTargetSelect.componentCode,
                    componentCodeVer: orderPurchaseDetailTargetSelect.componentCodeVer,
                    componentName: orderPurchaseDetailTargetSelect.componentName,

                    modelName: orderPurchaseDetailTargetSelect.modelName,
                    modelPartName: orderPurchaseDetailTargetSelect.modelPartName,
                    partNo: orderPurchaseDetailTargetSelect.partNo,
                    partSeq: orderPurchaseDetailTargetSelect.partSeq,
                    material: orderPurchaseDetailTargetSelect.material,
                    t_size: orderPurchaseDetailTargetSelect.t_size,
                    t_delta: orderPurchaseDetailTargetSelect.t_delta,
                    w_size: orderPurchaseDetailTargetSelect.w_size,
                    w_delta: orderPurchaseDetailTargetSelect.w_delta,
                    l_size: orderPurchaseDetailTargetSelect.l_size,
                    l_delta: orderPurchaseDetailTargetSelect.l_delta,

                    processingPlaneCount: 6, // default value
                };

                for (const column of schema.columns) {
                    if (column?.defaultValue) {
                        newFormData[column.name] = column.defaultValue;
                    }
                }

                setOkButtonDisabled(false);

                return newFormData;
            });

            let modifiedSchema = cloneDeep(schema);

            if (modifiedSchema) {
                for (const column of modifiedSchema.columns) {
                    switch (column.name) {
                        case "materialCode":
                        case "materialCodeVer":
                            column.hideInForm = true;
                            break;

                        case "componentCode":
                        case "componentCodeVer":
                        case "componentName":
                            column.hideInForm = false;
                            break;

                        case "modelName":
                        case "modelPartName":
                        case "partNo":
                        case "partSeq":
                        case "material":
                        case "t_size":
                        case "t_delta":
                        case "w_size":
                        case "w_delta":
                        case "l_size":
                        case "l_delta":
                            column.hideInForm = false;
                            break;

                        case "qty":
                        case "description":
                        case "processingPlaneCount":
                            column.disabled = false;
                            break;

                        default:
                            column.disabled = true;
                            break;
                    }
                }
                setModifiedSchema(modifiedSchema);
            }
        }
    }, [schema, orderPurchaseMstSelect, orderPurchaseDetailTargetSelect]);

    const checkItemValidataion = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("create", column, column.validation, formData[column.name] || "");

            if (!valid) {
                notifyWarning(msg);
                return false;
            }
        }

        if (orderPurchaseDetailTargetSelect) {
            if (formData.qty < orderPurchaseDetailTargetSelect.qtyOrderPurchase) {
                const msg = `${t("columnName.qty")} should be >= ${orderPurchaseDetailTargetSelect.qtyOrderPurchase}`;
                notifyWarning(msg);
                return false;
            }
        }

        return true;
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const onNotifySuccess = (msg) => {
        notifySuccess(msg);

        if (onDialogUpdateDetail) {
            onDialogUpdateDetail();
        }
    };

    const onDialogInitialized = (instance) => {
        setDialogInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onApiSuccess = (result) => {
        if (orderPurchaseDetailTargetApi) {
            orderPurchaseDetailTargetApi.refresh();
        }

        setKeepOpen((keepOpen) => {
            if (!keepOpen) {
                dialogInstance.hide();
            }

            if (onSuccess) {
                setTimeout(() => onSuccess(result.data), 0);
            } else {
                onNotifySuccess(result.data);
            }

            return keepOpen;
        });
    };

    const onApiFailure = (reason) => {
        if (customizeErrorMsgCallback) {
            notifyFailure(customizeErrorMsgCallback(reason));
        } else {
            notifyFailure(
                t("error.insertFailure", { reason: reason.response ? reason.response.data : JSON.stringify(reason) })
            );
        }
    };

    const onOk = (schema, formData) => {
        if (!checkItemValidataion(schema, formData)) {
            console.error("Form Validation Failed");

            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                notifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        // build base api url

        const url = `/api/${schema.name}/${orderPurchaseMstSelect.orderPurchaseId}`;

        const apiData = { ...formData };

        const form = new FormData();
        const json = {};
        const deleted = {};

        for (const formItem in apiData) {
            if (apiData[formItem] !== undefined) {
                json[formItem] = apiData[formItem];
            }
        }

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        apiClient
            .post(url, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    };

    const onCancel = () => {
        setOkButtonDisabled(true);
        setFormData(null);

        dialogInstance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </>
        );
    };

    const onChangeFormData = (formData) => {
        setTimeout(() => {
            setOkButtonDisabled(false);

            setFormData(formData);
        }, 0);
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const onCloseDialog = () => {
        setOkButtonDisabled(true);

        setFormData(null);

        if (onClose) {
            onClose();
        }
    };

    return (
        <Dialog
            id={id}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth="lg"
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <Divider />

            <DialogContent style={{ padding: "0px", height: "600px" }}>
                <ReflexContainer orientation="vertical">
                    <ReflexElement flex={0.5}>
                        <BasicCrudScreenType
                            id="TkVwOrderPurchaseDetailTargetPstMaterialList"
                            view="TkVwOrderPurchaseDetailTargetPstMaterialList"
                            headerTitle={t("term.pstMaterialList")}
                            noCreate
                            noEdit
                            noDelete
                            noExcel
                            embededInOtherScreenType
                            onInitialized={(grid) => {
                                grid.selectionMode = "Row";
                                setOrderPurchaseDetailTargetGrid(grid);
                            }}
                            onApiInitialized={(api) => {
                                setOrderPurchaseDetailTargetApi(api);
                            }}
                        />
                    </ReflexElement>

                    <ReflexSplitter style={{ width: "1px", color: "lightgray" }} />

                    <ReflexElement flex={0.5}>
                        <div style={{ padding: "16px" }}>
                            <AutoForm
                                id="order-purchase-detail-pstMaterialRegister-form"
                                schema={modifiedSchema}
                                mode="edit"
                                initialData={formData}
                                onChange={onChangeFormData}
                            />
                        </div>
                    </ReflexElement>
                </ReflexContainer>

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <Divider />

            <DialogActionsOkCancel
                firstActionComponent={checkKeepOpen}
                labels={[t("common.add")]}
                buttonDisabled={[okButtonDisabled, false]}
                onOk={() => onOk(modifiedSchema, formData)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default MomSysOrderPurchaseDetailPstMaterialRegisterDialog;
